/**
 * Seeded randomness. Everything the engine randomizes derives from one seed
 * string via cyrb128 → sfc32, with sub-streams drawn in the FIXED order
 * documented in deriveStreams(). That order is part of the determinism
 * contract: reordering it silently changes every existing shared seed URL.
 */

/** cyrb128 string hash → four 32-bit words (public domain, by bryc). */
export function cyrb128(str: string): [number, number, number, number] {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < str.length; i++) {
    const k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}

/** sfc32 PRNG; returns floats in [0, 1). */
export function sfc32(
  a: number,
  b: number,
  c: number,
  d: number,
): () => number {
  return () => {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

export const MAX_OCTAVES = 4;

/** Noise domain offsets live in [0, OFFSET_RANGE)² — large enough to
 * decorrelate seeds, small enough that fp32 lattice math stays clean. */
export const OFFSET_RANGE = 512;

export interface SeedStreams {
  /** Per-octave noise domain offsets (MAX_OCTAVES entries, drawn first). */
  octaveOffsets: Array<[number, number]>;
  /** uint32 salt for GPU integer spawn hashing (drawn second). */
  gpuSalt: number;
  /** Remaining stream: initial particle state generation (drawn last). */
  particles: () => number;
}

/** Derive all engine randomness from a seed string, in fixed order. */
export function deriveStreams(seed: string): SeedStreams {
  const rand = sfc32(...cyrb128(seed));
  const octaveOffsets: Array<[number, number]> = [];
  for (let i = 0; i < MAX_OCTAVES; i++) {
    octaveOffsets.push([rand() * OFFSET_RANGE, rand() * OFFSET_RANGE]);
  }
  const gpuSalt = Math.floor(rand() * 4294967296) >>> 0;
  return { octaveOffsets, gpuSalt, particles: rand };
}

/** Short random base36 token for fresh seeds. Non-deterministic — UI use only. */
export function newSeedString(): string {
  return Math.floor(Math.random() * 4294967296)
    .toString(36)
    .padStart(7, "0");
}
